/**
 * ENG-5399 — Tier 1 responsiveness probe (manager-side).
 *
 * Cheap, fast-cadence canary that catches "agent went silent" inside
 * minutes, well before the existing synthetic-probe cron's ~35 min
 * staleness window (`SyntheticReplyAgeSeconds`, ENG-5122).
 *
 * Mechanism: for each managed agent, read the mtime of the agent's
 * `pane.log` and report `now - mtime` as `PaneActivityAgeSeconds` via
 * a new `/host/responsiveness-probe` endpoint. `pane.log` is the
 * tmux pipe-pane sink set up by `setupPaneLog()` — any visible
 * activity (assistant turns, tool calls, in-place progress
 * heartbeats) bumps its mtime. A silent agent has a steadily
 * climbing age that lands in CloudWatch and trips a per-agent alarm.
 *
 * ENG-6017 adds a second per-agent signal on the same cadence:
 * `pending_inbound_oldest_age_seconds` — the age of the oldest marker
 * file across the agent's `*-pending-inbound/` directories (written by
 * the channel MCP servers for inbounds awaiting delivery). This is the
 * one artifact of the "message typed but never submitted" failure mode
 * that every other canary is blind to: in the koda incident
 * (2026-06-04) an operator Slack DM sat undelivered for 40+ minutes
 * while pane-activity stayed fresh (health checks), synthetic probes
 * were answered by the one-shot fallback, and heartbeat/session-alive
 * only reflect manager health. The field is OMITTED (not zero) when the
 * agent has no pending-inbound markers — the API treats absent as
 * "no signal", never as "healthy" (absent-vs-zero matters for
 * mixed-version fleets where old CLIs don't report it at all).
 *
 * Run from `pollCycle()` in `manager-worker.ts` on a configurable
 * interval (default 60s via `AUGMENTED_RESPONSIVENESS_INTERVAL_MS`;
 * lowered from 5 min by ENG-7550 - this post is what refreshes
 * `agents.last_pane_activity_at`, the minutely busy-sampling substrate).
 */

import { mkdirSync, readdirSync, readFileSync, renameSync, statSync, unlinkSync } from 'node:fs';
import { dirname, join } from 'node:path';
import { outstandingPromiseOldestAgeSeconds } from './interim-promise-scan.js';
import { paneLogPath } from './persistent-session.js';
import { DEFLECTION_CAUSES } from '@augmented/core/channels/deflection-causes.js';

export interface ResponsivenessProbeResult {
  code_name: string;
  pane_activity_age_seconds: number;
  /**
   * ENG-6017: age (s) of the oldest marker file across the agent's
   * `*-pending-inbound/` directories. Omitted when no markers exist —
   * absent means "no signal", NOT "zero / healthy".
   */
  pending_inbound_oldest_age_seconds?: number;
  /**
   * ENG-10045: how many DEAD-LETTERED inbound markers are on the host for this
   * agent RIGHT NOW - the files in its `*-pending-inbound-stale/` dirs, which is
   * the `store: stale` set `debug_inspect_dead_letters` reads.
   *
   * ALWAYS PRESENT, INCLUDING ZERO, and that is the whole point of the field
   * rather than an oversight against the convention its neighbours follow.
   *
   * Every other counter here is omitted when empty, because for an AGE an absent
   * field honestly means "no signal". This one feeds a CloudWatch gauge whose
   * alarm carries `OKActions`, and on that path "no datapoint" and "the publisher
   * stopped" are the same observation - `TreatMissingData: 'notBreaching'` reads
   * both as fine and CLOSES the alert. An affirmative zero is the difference
   * between an OK that means "nothing is stranded" and an OK that means "nobody
   * told me". ENG-10045 exists because an alarm said the first while meaning
   * something closer to the second.
   */
  dead_lettered_inbound_markers?: number;
  /**
   * ENG-8265: age (s) of the oldest OUTSTANDING interim promise — an inbound the
   * agent answered with `interim: true` ("the real answer is still coming") and
   * has not yet answered substantively. Measured from the marker's `promised_at`,
   * so it survives a mid-turn respawn and cannot be reset by unrelated engagement.
   * Omitted when the agent owes no promise (the healthy steady state) — absent
   * means "no signal", NOT "zero / healthy". See
   * `@augmented/core/channels/interim-promise.js` for why a `seen_at` marker is
   * not a promise.
   */
  promise_outstanding_oldest_age_seconds?: number;
  /**
   * ENG-6327: per-cause inbound deflection counts since the last probe (read +
   * RESET from the channel servers' `<channel>-deflections.json` counter files).
   * Omitted when there were none. Keyed by DeflectionCause.
   */
  deflections?: Record<string, number>;
  /**
   * ENG-6408 / ADR-0024 Slice 1.5: shadow lane-classification counts since the
   * last probe (read + RESET from `<channel>-lane-classifications.json`). Keyed
   * by `<lane>|<expects_reply>|<source>` plus the reserved
   * `suspected_misclassification` canary. Omitted when there were none.
   */
  lane_classifications?: Record<string, number>;
  /**
   * WS0 (Slack cross-thread reply routing): SHADOW reply-target classification
   * counts since the last probe (read + RESET from
   * `<channel>-reply-target-classifications.json`). Keyed by
   * `<result>|<pending_threads_bucket>`. Omitted when there were none.
   */
  slack_reply_target_classifications?: Record<string, number>;
  /**
   * WS2 (Slack cross-thread reply routing): SHADOW reply-BINDING classification
   * counts since the last probe (read + RESET from
   * `<channel>-reply-binding-classifications.json`). Keyed by the binding
   * classification (bound / coords_corrected / ... / unknown_inbound_id).
   * Omitted when there were none.
   */
  slack_reply_binding_classifications?: Record<string, number>;
  /**
   * ENG-7855: slack-hot-thread-guard (ENG-7462) SHADOW/enforce outcome counts
   * since the last probe (read + RESET from
   * `<channel>-hot-thread-classifications.json`). Keyed by
   * `<mode>|<outcome>|<proactive>`. Omitted when there were none (the guard only
   * records in shadow/enforce, so off-mode agents never populate this).
   */
  slack_hot_thread_classifications?: Record<string, number>;
  /**
   * ENG-8346: direct-chat cursor advances that moved FEWER rows than were asked
   * for, since the last probe (read + RESET from
   * `<source>-cursor-advance-classifications.json`). Keyed by
   * `<route>|<reason>|<partial>`. Omitted when there were none — which is the
   * healthy state, so a populated field is always worth reading.
   */
  direct_chat_cursor_shortfall_classifications?: Record<string, number>;
}

/**
 * ENG-8090: what actually goes on the wire to `/host/responsiveness-probe`.
 *
 * Differs from `ResponsivenessProbeResult` in two ways, both because opencode
 * agents have no `pane.log`:
 *
 *   - `pane_activity_age_seconds` is OPTIONAL. An opencode agent reports
 *     occupancy and turn completion instead; the API tolerates its absence and
 *     simply writes no `last_pane_activity_at` for that agent.
 *   - two opencode-only fields carry the split signals.
 *
 * Kept as a separate type so `collectResponsivenessProbes` — whose result the
 * WEDGE detector consumes and which must keep requiring a real pane age — is not
 * widened. See `collectPanelessActivityProbes`.
 */
export interface ResponsivenessProbePayloadEntry
  extends Omit<ResponsivenessProbeResult, 'pane_activity_age_seconds'> {
  pane_activity_age_seconds?: number;
  /**
   * ENG-8090: seconds since the agent was last doing work — 0 while a turn is in
   * flight. The OCCUPANCY signal, and the substrate for busy/idle sampling and
   * agent-hours billing. Omitted when the manager has observed no turn for this
   * agent: absent is "no signal", never "idle".
   *
   * Deliberately NOT a liveness signal. A serve wedged mid-turn reports 0 here.
   */
  busy_activity_age_seconds?: number;
  /**
   * ENG-8090: seconds since a turn last produced real reply text. The LIVENESS
   * signal — "turns are completing", not "the process printed something" — and
   * the only one of the two that may resolve a synthetic probe. Omitted when no
   * turn has ever succeeded in this manager generation.
   */
  turn_completion_age_seconds?: number;
  /**
   * ENG-8116: MEASURED occupancy, as whole closed wall-clock minutes.
   *
   * This is what replaces cadence-counted buckets. The two `*_age_seconds`
   * fields above describe an INSTANT, which forced the API to infer duration
   * from how often probes arrived — and probe arrival is 87s apart at the fleet
   * median, 182s at p90, against a 60s sampling window. Those buckets are
   * measured host-side instead, so the total is invariant to how often this
   * payload is sent.
   *
   * Only closed minutes appear here; the in-progress minute is still accruing
   * and is reported once it closes. Absent means "no occupancy to report",
   * which is distinct from an agent that reported none because it is on an old
   * CLI — the API keeps the legacy freshness path for those.
   */
  busy_buckets?: Array<{ bucket: string; seconds: number }>;
  /**
   * ENG-8465: SHADOW of the occupancy-qualification gate — per-drain counts of
   * how the reported `busy_buckets` split against transcript evidence of real
   * work (`occupancy-gate.ts` + the merged `@augmented/core` qualifier). Keyed by
   * `qualified` (a bucket with a real turn nearby — the positive control),
   * `unqualified_dropped` (no qualifying turn near it — dropped under `enforce`,
   * still billed under `shadow`), and `unreadable` (transcripts unreadable, so
   * the buckets were credited UNGATED — fail-open). Omitted when there were none.
   * See KNOWN_OCCUPANCY_QUALIFICATION_OUTCOMES; loss on a failed POST is accepted
   * (shadow telemetry, never re-credited like `busy_buckets`).
   */
  occupancy_qualification_classifications?: Record<string, number>;
}

/**
 * ENG-8465: the only occupancy-qualification outcome keys. Mirrored EXACTLY in
 * packages/api/src/routes/host-runtime.ts and enforced by
 * scripts/check-classification-allowlist-parity.mjs — a key present on one side
 * only is silently dropped, reading as a zero that means "never ingested". Add a
 * new key to BOTH sets in the same change.
 */
const KNOWN_OCCUPANCY_QUALIFICATION_OUTCOMES = new Set([
  'qualified',
  'unqualified_dropped',
  'unreadable',
  // ENG-8571: bucket kept (never dropped) because the bounded transcript tail did
  // not reach back far enough to judge it. MUST also be in the API-side set
  // (routes/host-runtime.ts) or the parity guard drops it to a silent zero.
  'coverage_shortfall',
  // ENG-9178: bucket kept because it overlaps a COMPLETED tool-call bracket with
  // no qualifying turn in reach — the middle of a long tool call. Separate from
  // `qualified` so the flip can be sized against turn evidence and bracket
  // evidence independently. Mirror in the API set.
  'qualified_by_bracket',
  // ENG-9178: bucket HELD (neither reported nor dropped) because it sits inside a
  // tool call that has not returned. Under `enforce` this is also the signal that
  // stands the legacy cron down for a drain that reported no buckets at all —
  // see the ENG-8625 note on hasOccupancyQualification. Mirror in the API set.
  'bracket_deferred',
]);

/**
 * Build the wire classification record from a {@link QualifyBucketsResult}-shaped
 * count, keeping only known, positive-integer outcomes. Returns null when empty
 * so the caller omits the field entirely (absent = "no signal"). Keeping the
 * builder here — not in occupancy-gate.ts — keeps KNOWN_OCCUPANCY_QUALIFICATION_
 * OUTCOMES the single CLI-side allowlist the parity guard diffs against the API.
 */
export function occupancyQualificationClassifications(counts: {
  qualified: number;
  unqualifiedDropped: number;
  unreadable: number;
  coverageShortfall: number;
  qualifiedByBracket?: number;
  bracketDeferred?: number;
}): Record<string, number> | null {
  const raw: Record<string, number> = {
    qualified: counts.qualified,
    unqualified_dropped: counts.unqualifiedDropped,
    unreadable: counts.unreadable,
    coverage_shortfall: counts.coverageShortfall,
    // ENG-9178. Optional on the input type so the manager's fail-open fallback
    // (which builds this record by hand when the qualifier throws) does not have
    // to know about outcomes it can never produce.
    qualified_by_bracket: counts.qualifiedByBracket ?? 0,
    bracket_deferred: counts.bracketDeferred ?? 0,
  };
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (KNOWN_OCCUPANCY_QUALIFICATION_OUTCOMES.has(key) && Number.isSafeInteger(value) && value > 0) {
      out[key] = value;
    }
  }
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * ENG-6327 — per-channel deflection counter file-layout contract. The channel
 * MCP servers increment `<channel>-deflections.json` in the agent home (via
 * recordChannelDeflection in packages/mcp/ack-reaction.ts); apps/cli does not
 * depend on @integrity-labs/mcp, so the manager re-implements the read against
 * this contract — the same read-only, no-IPC posture as the `*-pending-inbound`
 * scan above. Body is a JSON object keyed by cause with integer counts.
 */
const DEFLECTION_COUNTER_SUFFIX = '-deflections.json';
// ENG-10054: this list used to be re-declared here. The comment it carried was
// right about the danger - "adding a cause in packages/mcp WITHOUT adding it
// here produces a metric that is faithfully written and never observed" - and a
// parity test was built to enforce it. But the test only ever compared THIS copy
// with the mcp one, and the drift happened in the THIRD copy nobody was
// watching: the API ingest filter, missing `aged_out_unrecoverable` and
// `marker_corrupt`. The warning was correct and the guard was pointed at the
// wrong pair. There is now one list, in core, and no pair to keep in step.
//
// (The note above about apps/cli not depending on @integrity-labs/mcp still
// holds - this imports @augmented/core, which apps/cli already depends on, so
// the no-IPC read-only posture is unchanged.)
const KNOWN_DEFLECTION_CAUSES = DEFLECTION_CAUSES;

/**
 * Read and RESET every `<channel>-deflections.json` in an agent home, returning
 * the summed counts by cause (or null when there are none). Consumes via atomic
 * rename-then-read-then-unlink so an increment racing the reset is carried into
 * the next window rather than lost. Only known causes are accumulated so a
 * corrupt file can't smuggle an unbounded dimension into CloudWatch.
 *
 * Reset-on-read is required: these are deltas-since-last-consume, so the metric
 * would over-count if they weren't cleared each cycle. The trade-off is that a
 * failed POST loses that cycle's counts (under-count) — acceptable for a
 * frequency metric and consistent with the other best-effort probe siblings;
 * unlike the in-memory give-up counter, file-based counts aren't re-credited
 * on POST failure.
 */
export function readAndResetChannelDeflections(
  agentHomeDir: string,
): Record<string, number> | null {
  let names: string[];
  try {
    names = readdirSync(agentHomeDir);
  } catch {
    return null;
  }
  const total: Record<string, number> = {};
  let any = false;
  for (const name of names) {
    if (!name.endsWith(DEFLECTION_COUNTER_SUFFIX)) continue;
    const full = join(agentHomeDir, name);
    const consuming = `${full}.consuming`;
    try {
      renameSync(full, consuming); // atomic; replaces any stale .consuming from a crashed cycle
    } catch {
      continue; // vanished / not a regular file — skip
    }
    try {
      const parsed = JSON.parse(readFileSync(consuming, 'utf8')) as Record<string, unknown>;
      if (parsed && typeof parsed === 'object') {
        for (const cause of KNOWN_DEFLECTION_CAUSES) {
          const n = parsed[cause];
          if (typeof n === 'number' && Number.isFinite(n) && n > 0) {
            total[cause] = (total[cause] ?? 0) + Math.floor(n);
            any = true;
          }
        }
      }
    } catch {
      /* corrupt — drop it */
    }
    try {
      unlinkSync(consuming);
    } catch {
      /* non-fatal */
    }
  }
  return any ? total : null;
}

/**
 * ENG-6408 / ADR-0024 Slice 1.5 - per-channel lane-classification counter
 * file-layout contract. The channel MCP servers increment
 * `<channel>-lane-classifications.json` in the agent home (via
 * recordLaneClassification in packages/mcp/inbound-lane-telemetry.ts); apps/cli
 * does not depend on @integrity-labs/mcp, so the manager re-implements the read
 * against this contract (same posture as the deflection scan above). Body is a
 * JSON object keyed by `<lane>|<expects_reply>|<source>` (plus the reserved
 * `suspected_misclassification`) with integer counts.
 */
const LANE_CLASSIFICATION_COUNTER_SUFFIX = '-lane-classifications.json';
const SUSPECTED_MISCLASSIFICATION_KEY = 'suspected_misclassification';
const KNOWN_LANES = new Set(['conversational', 'directive', 'liveness']);
const KNOWN_LANE_SOURCES = new Set(['slack', 'telegram', 'msteams', 'direct-chat']);

/**
 * Only well-formed composite keys (and the misclassification canary) are
 * accumulated, so a corrupt counter file can't smuggle an unbounded dimension
 * into CloudWatch - the same bounding the deflection reader applies.
 */
function isKnownLaneClassificationKey(key: string): boolean {
  if (key === SUSPECTED_MISCLASSIFICATION_KEY) return true;
  const parts = key.split('|');
  if (parts.length !== 3) return false;
  const [lane, expectsReply, source] = parts as [string, string, string];
  return (
    KNOWN_LANES.has(lane) &&
    (expectsReply === 'true' || expectsReply === 'false') &&
    KNOWN_LANE_SOURCES.has(source)
  );
}

/**
 * Read and RESET every `<channel>-lane-classifications.json` in an agent home,
 * returning the summed counts by key (or null when there are none). Mirrors
 * readAndResetChannelDeflections exactly: atomic rename-then-read-then-unlink so
 * an increment racing the reset carries into the next window, and only known
 * keys are accumulated. SHADOW telemetry - informs the Slice 2 gate, gates
 * nothing itself.
 */
export function readAndResetChannelLaneClassifications(
  agentHomeDir: string,
): Record<string, number> | null {
  let names: string[];
  try {
    names = readdirSync(agentHomeDir);
  } catch {
    return null;
  }
  const total: Record<string, number> = {};
  let any = false;
  for (const name of names) {
    if (!name.endsWith(LANE_CLASSIFICATION_COUNTER_SUFFIX)) continue;
    const full = join(agentHomeDir, name);
    const consuming = `${full}.consuming`;
    try {
      renameSync(full, consuming); // atomic; replaces any stale .consuming from a crashed cycle
    } catch {
      continue; // vanished / not a regular file — skip
    }
    try {
      const parsed = JSON.parse(readFileSync(consuming, 'utf8')) as Record<string, unknown>;
      if (parsed && typeof parsed === 'object') {
        for (const [key, raw] of Object.entries(parsed)) {
          if (!isKnownLaneClassificationKey(key)) continue;
          if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
            total[key] = (total[key] ?? 0) + Math.floor(raw);
            any = true;
          }
        }
      }
    } catch {
      /* corrupt — drop it */
    }
    try {
      unlinkSync(consuming);
    } catch {
      /* non-fatal */
    }
  }
  return any ? total : null;
}

/**
 * WS0 (Slack cross-thread reply routing) - per-channel reply-target counter
 * file-layout contract. The channel MCP servers increment
 * `<channel>-reply-target-classifications.json` in the agent home (via
 * recordReplyTargetClassification in packages/mcp/slack-reply-target-telemetry.ts);
 * apps/cli does not depend on @integrity-labs/mcp, so the manager re-implements
 * the read against this contract (same posture as the lane-classification scan
 * above). Body is a JSON object keyed by `<result>|<pending_threads_bucket>`
 * with integer counts.
 */
const REPLY_TARGET_CLASSIFICATION_COUNTER_SUFFIX = '-reply-target-classifications.json';
const KNOWN_REPLY_TARGET_RESULTS = new Set([
  'matched_pending',
  'matched_recent',
  'crossed_candidate',
  'unknown_target',
]);
const KNOWN_PENDING_THREAD_BUCKETS = new Set(['0', '1', '2', '3plus']);

/**
 * Only well-formed `<result>|<bucket>` keys are accumulated, so a corrupt counter
 * file can't smuggle an unbounded dimension into CloudWatch - the same bounding
 * the lane-classification / deflection readers apply.
 */
function isKnownReplyTargetKey(key: string): boolean {
  const parts = key.split('|');
  if (parts.length !== 2) return false;
  const [result, bucket] = parts as [string, string];
  return KNOWN_REPLY_TARGET_RESULTS.has(result) && KNOWN_PENDING_THREAD_BUCKETS.has(bucket);
}

/**
 * Read and RESET every `<channel>-reply-target-classifications.json` in an agent
 * home, returning the summed counts by key (or null when there are none). Mirrors
 * readAndResetChannelLaneClassifications exactly: atomic
 * rename-then-read-then-unlink so an increment racing the reset carries into the
 * next window, and only known keys are accumulated. SHADOW telemetry - informs
 * the WS2 binding gate, gates nothing itself.
 */
export function readAndResetSlackReplyTargetClassifications(
  agentHomeDir: string,
): Record<string, number> | null {
  let names: string[];
  try {
    names = readdirSync(agentHomeDir);
  } catch {
    return null;
  }
  const total: Record<string, number> = {};
  let any = false;
  for (const name of names) {
    if (!name.endsWith(REPLY_TARGET_CLASSIFICATION_COUNTER_SUFFIX)) continue;
    const full = join(agentHomeDir, name);
    const consuming = `${full}.consuming`;
    try {
      renameSync(full, consuming); // atomic; replaces any stale .consuming from a crashed cycle
    } catch {
      continue; // vanished / not a regular file - skip
    }
    try {
      const parsed = JSON.parse(readFileSync(consuming, 'utf8')) as Record<string, unknown>;
      if (parsed && typeof parsed === 'object') {
        for (const [key, raw] of Object.entries(parsed)) {
          if (!isKnownReplyTargetKey(key)) continue;
          if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
            total[key] = (total[key] ?? 0) + Math.floor(raw);
            any = true;
          }
        }
      }
    } catch {
      /* corrupt - drop it */
    }
    try {
      unlinkSync(consuming);
    } catch {
      /* non-fatal */
    }
  }
  return any ? total : null;
}

// WS2 (Slack cross-thread reply routing) - per-channel reply-BINDING counter
// file-layout contract. The slack MCP increments
// `<channel>-reply-binding-classifications.json` (via
// recordReplyBindingClassification in packages/mcp/slack-reply-binding-telemetry.ts);
// apps/cli re-implements the read against this contract, same posture as the
// reply-target scan above. Body is a JSON object keyed by the binding
// classification (plus the reserved unknown_inbound_id canary) with integer counts.
const REPLY_BINDING_CLASSIFICATION_COUNTER_SUFFIX = '-reply-binding-classifications.json';
const KNOWN_REPLY_BINDING_KEYS = new Set([
  'bound',
  'coords_corrected',
  'coords_match',
  'proactive',
  'unknown_target',
  'legacy',
  'unknown_inbound_id', // reserved canary
  // ENG-7716: the ENG-7542 DM-channel guard writes these into the same counter
  // file. They are additional reserved keys (channel_mistarget_corrected counts
  // every cross-channel rewrite; dm_channel_mistarget its DM subset), overlapping
  // the main classifications the same way unknown_inbound_id does. Allowlisted so
  // the DM->channel leak signal survives the read/reset fold and reaches CloudWatch.
  'channel_mistarget_corrected',
  'dm_channel_mistarget',
  // ENG-7806 (fm1): the inbound-delivery-ledger verdicts the Stop hook records for
  // each recover_*_for decision (self = this inbound already delivered; moved_on =
  // a different conversation was answered after this inbound; clear = ledger says
  // recover; fallback = no ledger data, used the transcript scan). Folded into the
  // same counter-file pipe so ledger coverage reaches CloudWatch as Classification
  // dimensions on the existing SlackReplyBindingClassifications metric.
  'delivery_ledger_self',
  'delivery_ledger_moved_on',
  'delivery_ledger_clear',
  'delivery_ledger_fallback',
  // ENG-8137: the scheduled-turn channel guard writes these into the same counter
  // file (recordScheduledChannelOverride). `_shadow` counts detections while the
  // flag observes without changing routing - the false-positive signal that gates
  // the enforce flip; `_blocked` counts sends actually refused under enforce.
  // Allowlisted so both reach CloudWatch: without them this guard would fire
  // fleet-wide with no observable rate, which is the blindness that let the
  // original misroute reach customers unnoticed.
  'scheduled_channel_override_shadow',
  'scheduled_channel_override_blocked',
]);

/**
 * Read and RESET every `<channel>-reply-binding-classifications.json` in an agent
 * home, returning the summed counts by classification (or null when there are
 * none). Mirrors readAndResetSlackReplyTargetClassifications; only known keys are
 * accumulated so a corrupt file can't add an unbounded CloudWatch dimension.
 * SHADOW telemetry - informs the enforce flip, gates nothing itself.
 */
export function readAndResetSlackReplyBindingClassifications(
  agentHomeDir: string,
): Record<string, number> | null {
  let names: string[];
  try {
    names = readdirSync(agentHomeDir);
  } catch {
    return null;
  }
  const total: Record<string, number> = {};
  let any = false;
  for (const name of names) {
    if (!name.endsWith(REPLY_BINDING_CLASSIFICATION_COUNTER_SUFFIX)) continue;
    const full = join(agentHomeDir, name);
    const consuming = `${full}.consuming`;
    try {
      renameSync(full, consuming);
    } catch {
      continue;
    }
    try {
      const parsed = JSON.parse(readFileSync(consuming, 'utf8')) as Record<string, unknown>;
      if (parsed && typeof parsed === 'object') {
        for (const [key, raw] of Object.entries(parsed)) {
          if (!KNOWN_REPLY_BINDING_KEYS.has(key)) continue;
          if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
            total[key] = (total[key] ?? 0) + Math.floor(raw);
            any = true;
          }
        }
      }
    } catch {
      /* corrupt - drop it */
    }
    try {
      unlinkSync(consuming);
    } catch {
      /* non-fatal */
    }
  }
  return any ? total : null;
}

// ENG-7855 - per-channel hot-thread-guard (ENG-7462) counter file-layout contract.
// The slack MCP increments `<channel>-hot-thread-classifications.json` (via
// recordHotThreadClassification in packages/mcp/slack-hot-thread-telemetry.ts);
// apps/cli re-implements the read against this contract, same posture as the
// reply-target / reply-binding scans above. Body is a JSON object keyed by
// `<mode>|<outcome>|<proactive>` with integer counts.
const HOT_THREAD_CLASSIFICATION_COUNTER_SUFFIX = '-hot-thread-classifications.json';
// Mirrored by the API-side sets of the same names in routes/host-runtime.ts.
// A value accepted by only one side is dropped silently and the metric reads a
// clean zero meaning "never ingested" - the ENG-7716 / ENG-8214 failure. Any
// new mode or outcome must land in BOTH, and in the mcp HotThreadGuardResult
// union, in the same change.
const KNOWN_HOT_THREAD_MODES = new Set(['shadow', 'enforce']);
const KNOWN_HOT_THREAD_OUTCOMES = new Set([
  'redirected_to_hot_thread',
  'root_forced',
  'root_no_hot_thread',
  'not_applicable',
]);

/**
 * Only well-formed `<mode>|<outcome>|<proactive>` keys are accumulated, so a
 * corrupt counter file can't smuggle an unbounded dimension into CloudWatch - the
 * same bounding the reply-target / reply-binding readers apply.
 */
function isKnownHotThreadKey(key: string): boolean {
  const parts = key.split('|');
  if (parts.length !== 3) return false;
  const [mode, outcome, proactive] = parts as [string, string, string];
  return (
    KNOWN_HOT_THREAD_MODES.has(mode) &&
    KNOWN_HOT_THREAD_OUTCOMES.has(outcome) &&
    (proactive === 'true' || proactive === 'false')
  );
}

/**
 * Read and RESET every `<channel>-hot-thread-classifications.json` in an agent
 * home, returning the summed counts by key (or null when there are none). Mirrors
 * readAndResetSlackReplyTargetClassifications exactly: atomic
 * rename-then-read-then-unlink so an increment racing the reset carries into the
 * next window, and only known keys are accumulated. SHADOW/enforce observability
 * for the hot-thread guard - gates nothing itself.
 */
export function readAndResetSlackHotThreadClassifications(
  agentHomeDir: string,
): Record<string, number> | null {
  let names: string[];
  try {
    names = readdirSync(agentHomeDir);
  } catch {
    return null;
  }
  const total: Record<string, number> = {};
  let any = false;
  for (const name of names) {
    if (!name.endsWith(HOT_THREAD_CLASSIFICATION_COUNTER_SUFFIX)) continue;
    const full = join(agentHomeDir, name);
    const consuming = `${full}.consuming`;
    try {
      renameSync(full, consuming);
    } catch {
      continue;
    }
    try {
      const parsed = JSON.parse(readFileSync(consuming, 'utf8')) as Record<string, unknown>;
      if (parsed && typeof parsed === 'object') {
        for (const [key, raw] of Object.entries(parsed)) {
          if (!isKnownHotThreadKey(key)) continue;
          // ENG-7855 (CodeRabbit): require a positive SAFE INTEGER. The writer only
          // ever emits integer counts, so a fractional/unsafe value is corruption -
          // flooring it would emit a spurious 0 datapoint (0.5) or silently alter a
          // count (1.9 → 1). Drop it instead.
          if (typeof raw === 'number' && Number.isSafeInteger(raw) && raw > 0) {
            total[key] = (total[key] ?? 0) + raw;
            any = true;
          }
        }
      }
    } catch {
      /* corrupt - drop it */
    }
    try {
      unlinkSync(consuming);
    } catch {
      /* non-fatal */
    }
  }
  return any ? total : null;
}

// ENG-8346 - direct-chat cursor-advance shortfall counter file-layout contract.
// Two writers increment `<source>-cursor-advance-classifications.json` via
// recordCursorAdvanceOutcome (@augmented/core/direct-chat/cursor-advance-
// telemetry.js): the direct-chat channel MCP writes `direct-chat-...` and the
// manager's ADR-0047 opencode arm writes `direct-chat-manager-...`. Separate
// files because they are separate PROCESSES and read-modify-write on one file
// loses increments. Both are summed here. Body is a JSON object keyed by
// `<route>|<reason>|<partial>` with integer counts.
const CURSOR_SHORTFALL_COUNTER_SUFFIX = '-cursor-advance-classifications.json';
// Mirrored by the API-side sets of the same names in routes/host-runtime.ts, and
// sourced from CURSOR_SHORTFALL_REASONS in @augmented/core's direct-chat/
// cursor-advance.ts (which the WRITER normalises against, so an unrecognised
// server reason arrives here already folded into `other` rather than dropped).
// A value accepted by only one side is filtered out with no error and no log,
// and the metric then reads a clean zero meaning "never ingested" - the
// ENG-7716 / ENG-8214 failure. Any new route or reason must land in BOTH.
// ENG-8393: the last three are the NON-SHORTFALL outcomes. `advanced` and
// `advanced_unreported` are benign; `failed` is not - it records an advance
// that did not happen, and is counted because a fleet where every advance
// failed would otherwise report zero shortfalls AND zero successes. The name of
// this set now under-describes it - since that ticket the reason slot also
// carries the non-shortfall verdicts, which is what gives the metric a
// denominator and makes a zero on the shortfall reasons mean something. Kept
// as-is because the name is load-bearing in the parity guard's REQUIRED_PAIRS
// and mirrored on the API side.
const KNOWN_CURSOR_ADVANCE_ROUTES = new Set(['reply', 'consume']);
const KNOWN_CURSOR_SHORTFALL_REASONS = new Set([
  'not_found',
  'unclaimed',
  'gave_up',
  'already_delivered',
  'error',
  'undiagnosed',
  'unknown',
  'unreported',
  'malformed_count',
  'other',
  'advanced',
  'advanced_unreported',
  'failed',
]);

/**
 * Only well-formed `<route>|<reason>|<partial>` keys are accumulated, so a
 * corrupt counter file cannot smuggle an unbounded dimension into CloudWatch -
 * the same bounding the reply-target / reply-binding / hot-thread readers apply.
 */
function isKnownCursorShortfallKey(key: string): boolean {
  const parts = key.split('|');
  if (parts.length !== 3) return false;
  const [route, reason, partial] = parts as [string, string, string];
  return (
    KNOWN_CURSOR_ADVANCE_ROUTES.has(route) &&
    KNOWN_CURSOR_SHORTFALL_REASONS.has(reason) &&
    (partial === 'true' || partial === 'false')
  );
}

/**
 * Read and RESET every `<source>-cursor-advance-classifications.json` in an agent
 * home, returning the summed counts by key (or null when there are none).
 *
 * Mirrors readAndResetSlackHotThreadClassifications exactly: atomic
 * rename-then-read-then-unlink so an increment racing the reset carries into the
 * next window, and only known keys are accumulated.
 *
 * This is the numerator AND (since ENG-8393) the denominator for ENG-8347. A
 * direct-chat cursor that does not advance strands a message the agent has
 * ALREADY answered, and the only other trace is a stderr line in a log nobody
 * watches - which is how the ENG-8337 strand went unnoticed.
 *
 * ENG-8393 changed the cost profile deliberately: the writer used to record only
 * shortfalls, so a healthy agent wrote no file at all - and that was the defect,
 * not a saving. It made "no shortfalls happened" and "this host never ingested"
 * byte-identical, which for a metric that exists to gate a decision means the
 * decision is taken blind. A quiet agent still writes nothing (it advances no
 * cursors); an ACTIVE one now writes a small file per probe window either way.
 */
export function readAndResetDirectChatCursorShortfallClassifications(
  agentHomeDir: string,
): Record<string, number> | null {
  let names: string[];
  try {
    names = readdirSync(agentHomeDir);
  } catch {
    return null;
  }
  const total: Record<string, number> = {};
  let any = false;
  for (const name of names) {
    if (!name.endsWith(CURSOR_SHORTFALL_COUNTER_SUFFIX)) continue;
    const full = join(agentHomeDir, name);
    const consuming = `${full}.consuming`;
    try {
      renameSync(full, consuming);
    } catch {
      continue;
    }
    try {
      const parsed = JSON.parse(readFileSync(consuming, 'utf8')) as Record<string, unknown>;
      if (parsed && typeof parsed === 'object') {
        for (const [key, raw] of Object.entries(parsed)) {
          if (!isKnownCursorShortfallKey(key)) continue;
          // Require a positive SAFE INTEGER, same as the hot-thread reader: the
          // writer only ever emits integer counts, so a fractional/unsafe value
          // is corruption - flooring it would emit a spurious 0 datapoint or
          // silently alter a count.
          if (typeof raw === 'number' && Number.isSafeInteger(raw) && raw > 0) {
            total[key] = (total[key] ?? 0) + raw;
            any = true;
          }
        }
      }
    } catch {
      /* corrupt - drop it */
    }
    try {
      unlinkSync(consuming);
    } catch {
      /* non-fatal */
    }
  }
  return any ? total : null;
}

// ENG-7550: 60s default (was 5 min). The probe post is what refreshes
// agents.last_pane_activity_at, so its cadence is the resolution ceiling for
// minutely busy sampling (sample_agent_activity at p_window_seconds=60).
// Sized before lowering: the per-agent CloudWatch alarms already run
// Period:60/EvaluationPeriods:1 (built for 1-min datapoints), CloudWatch
// bills per metric not per datapoint, and the 5x Lambda/API-GW invocation
// increase is trivial at fleet scale. The reset-on-read delta counters
// (deflections, lane/reply classifications) are cadence-agnostic - smaller
// batches, same totals.
const DEFAULT_INTERVAL_MS = 60 * 1000;

export function getResponsivenessIntervalMs(): number {
  const raw = process.env.AUGMENTED_RESPONSIVENESS_INTERVAL_MS;
  if (!raw) return DEFAULT_INTERVAL_MS;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_INTERVAL_MS;
}

/**
 * ENG-10048: the age basis for one marker — its TRUE arrival time where the
 * marker records one, falling back to the file's mtime where it does not.
 *
 * WHY NOT JUST mtime. `pending-inbound-stale` measured mtime, and every
 * in-place marker rewrite advances it: `seen_at`, `promised_at` (ENG-8265), a
 * `replay_count` bump. So reading a stuck thread reset the clock that reported
 * it, and the gauge said "one hour" about a three-hour-old undelivered message.
 * The standard diagnostic disarmed the alarm, and nothing said so. Measured on
 * sherlock's own markers: `received_at` 17:01:56, mtime 19:00:59, `seen_at`
 * 19:00:59 — 8ms apart, so the read is unambiguously what moved it.
 *
 * WHY NOT JUST received_at, WHICH IS THE TRAP. The ticket's own AC said a
 * missing `received_at` must fail closed and be treated as old. That is right
 * for a corrupt marker and wrong for a whole CHANNEL: msteams markers carry the
 * raw Bot Framework Activity and have no `received_at` at all, while
 * `msteams-inbound-puller.ts` deliberately stamps their mtime from `created_at`
 * so boot-drain preserves arrival order. For that channel mtime ALREADY IS the
 * arrival time, and failing closed would have fired this alarm on every Teams
 * agent on the first evaluation with nothing stranded anywhere.
 *
 * So: prefer the semantic field, fall back to the incidental one per marker.
 * A marker is never SKIPPED for lacking a clock — it always contributes an age,
 * which preserves the fail-closed intent (a corrupt marker cannot mask a wedge)
 * without inventing one for a channel that never had the field.
 *
 * A future-dated `received_at` (clock skew) falls back to mtime rather than
 * reporting a negative age, matching `isPendingMarkerStale`'s treatment.
 *
 * `nowMs` is REQUIRED and is the caller's sampled clock, not `Date.now()` read
 * here (CodeRabbit on #5691). The collector computes the reported age from a
 * `nowMs` it captured earlier in the tick; reading the clock again inside this
 * function opens a window where a `received_at` that is future relative to the
 * SAMPLE is already past by the time it is checked. It then passes the
 * future-date guard, yields a negative age, and `Math.max(0, ...)` in the
 * collector clamps that to 0 - reporting a stranded marker as brand new and
 * suppressing the very alarm this change exists to make honest. Taking the
 * clock as a parameter makes the two reads the same read, and making it
 * required stops a caller silently reintroducing the skew.
 */
export function markerArrivalMs(markerPath: string, mtimeMs: number, nowMs: number): number {
  let receivedAt: unknown;
  try {
    receivedAt = (JSON.parse(readFileSync(markerPath, 'utf8')) as { received_at?: unknown })
      ?.received_at;
  } catch {
    return mtimeMs; // unreadable/malformed — mtime is the only clock available
  }
  if (typeof receivedAt !== 'string') return mtimeMs;
  const parsed = Date.parse(receivedAt);
  if (!Number.isFinite(parsed)) return mtimeMs;
  // Future-dated: skew, not a real arrival. mtime is the safer basis.
  if (parsed > nowMs) return mtimeMs;
  return parsed;
}

/**
 * ENG-10048: which markers the `pending-inbound-stale` ALARM counts.
 *
 * Named and exported rather than inlined because "which markers count" is the
 * decision that went wrong when this alarm was first armed, and it deserves one
 * home and a test rather than a condition inside a loop.
 *
 * This filter is DELIBERATELY NOT the wedge detector's filter, because the two
 * ask different questions. `oldestLivePendingInboundMtimeMs` asks "is THIS
 * SESSION failing to drain?"; the alarm asks "is a customer message stranded?".
 *
 *  - `discretionary: true` → EXCLUDED. Auto-followed participant chatter the
 *    agent may legitimately skip. A deliberate skip is not a stranded ask, and
 *    paging on one trains people to ignore the alarm.
 *  - `undeliverable: true` → EXCLUDED. Already dead-lettered by the channel and
 *    covered by its own `dead-lettered-inbound` alarm; counting it here pages
 *    twice for one problem. NOTE the standing caveat: that sibling alarm is
 *    itself reported to self-close while the message is still stranded, so
 *    coverage of dead-lettered markers rests on that being fixed, not on this.
 *  - pre-session markers → INCLUDED, unlike the wedge scan. A message that
 *    outlived a respawn is MORE stranded, not less. The wedge detector excludes
 *    them because a leftover cannot prove this session is stuck; the alarm does
 *    not care which session was up when the customer wrote in.
 */
export function isAlarmRelevantMarker(flags: MarkerFlags): boolean {
  return !flags.undeliverable && !flags.discretionary;
}

/**
 * ENG-6017: oldest pending-inbound marker ARRIVAL time (ms epoch) for an
 * agent, or null when it has no counted markers / no pending-inbound dirs.
 *
 * ENG-10048 changed TWO things here, and both were load-bearing:
 *   - the CLOCK is now `markerArrivalMs`, not the raw mtime, so an observer
 *     reading a stuck thread can no longer reset the age this alarm reports;
 *   - the marker SET is now filtered by `isAlarmRelevantMarker`. That is not
 *     optional tidying: without it, moving to a clock nobody can reset makes
 *     dead-lettered and discretionary markers - which never drain, by design -
 *     report their true age and hold the alarm on forever. The old mtime clock
 *     was accidentally masking them, so the filter has to arrive with it.
 *
 * The channel MCP servers (slack-channel, telegram-channel, …) write one
 * marker file per inbound into `~/.augmented/<codeName>/<channel>-pending-
 * inbound/` and clear it when the agent acknowledges the message. The
 * directory layout is the contract here — read-only, no IPC with the MCP
 * (the MCP and CLI release independently; file mtimes need no protocol).
 *
 * ENG-6072: only plain, non-hidden files count as markers. The msteams MCP
 * keeps `.markers/` and `.processed/` housekeeping SUBDIRECTORIES inside its
 * pending-inbound dir; their mtimes never advance, so statting every dirent
 * made the gauge climb forever and fired pending-inbound-stale on agents with
 * zero stranded messages (kylie ~3.4d / scout ~34h false ALARMs the moment
 * ENG-6023 activated the alarm). Dot-entries are skipped wholesale — the
 * hidden namespace is reserved for MCP bookkeeping, never for markers.
 */
function oldestPendingInboundArrivalMs(agentHomeDir: string, nowMs: number): number | null {
  let oldest: number | null = null;
  let entries;
  try {
    entries = readdirSync(agentHomeDir, { withFileTypes: true });
  } catch {
    return null; // agent home missing — nothing to report
  }
  for (const entry of entries) {
    if (!entry.isDirectory() || !entry.name.endsWith('-pending-inbound')) continue;
    const dir = join(agentHomeDir, entry.name);
    let files;
    try {
      files = readdirSync(dir, { withFileTypes: true });
    } catch {
      continue;
    }
    for (const file of files) {
      if (!file.isFile() || file.name.startsWith('.')) continue;
      const full = join(dir, file.name);
      let mtimeMs: number;
      try {
        mtimeMs = statSync(full).mtimeMs;
      } catch {
        // Marker drained between readdir and stat — that's the happy path.
        continue;
      }
      const flags = readMarkerFlags(full);
      if (flags === null) continue; // vanished mid-scan — drained, exclude
      if (flags !== 'malformed' && !isAlarmRelevantMarker(flags)) continue;
      const arrivalMs = markerArrivalMs(full, mtimeMs, nowMs);
      if (oldest === null || arrivalMs < oldest) oldest = arrivalMs;
    }
  }
  return oldest;
}

/**
 * ENG-6160 / ENG-6319: read a marker's classification flags.
 *  - `{...}`       → parsed flags.
 *  - `null`        → vanished mid-scan (ENOENT) — drained between stat and
 *                    read, the happy path; callers exclude it.
 *  - `'malformed'` → present but unreadable for another reason. The live
 *                    scan treats this as LIVE (a corrupt marker can never
 *                    mask a real wedge); park leaves it in place (a corrupt
 *                    marker must never become a dropped message).
 */
interface MarkerFlags {
  /** ENG-5846: dead-lettered by the channel (⏳-noticed, never drainable). */
  undeliverable: boolean;
  /**
   * ENG-6319: auto-followed participant-thread inbound the agent may
   * legitimately skip. Excluded from the wedge live-scan (a deliberate skip
   * must not read as "failing to drain" and force-respawn a healthy
   * session) but still parked across respawns — it may be a real
   * operator message awaiting a reply.
   */
  discretionary: boolean;
}

function readMarkerFlags(markerPath: string): MarkerFlags | null | 'malformed' {
  try {
    const parsed = JSON.parse(readFileSync(markerPath, 'utf8')) as {
      undeliverable?: unknown;
      discretionary?: unknown;
    };
    return {
      undeliverable: parsed?.undeliverable === true,
      discretionary: parsed?.discretionary === true,
    };
  } catch (error) {
    return (error as NodeJS.ErrnoException).code === 'ENOENT' ? null : 'malformed';
  }
}

/**
 * ENG-6160: oldest *LIVE* pending-inbound marker mtime (ms epoch) for an agent,
 * or null when there is no live marker. "Live" excludes:
 *
 *  - markers older than `sessionStartMs` — a marker written before the current
 *    session started is a leftover from a PREVIOUS session and cannot mean
 *    *this* session is failing to drain. This is the load-bearing exclusion:
 *    without it, an orphan marker survives a fresh respawn and the wedge
 *    detector re-fires forever on a healthy idle agent (the sherlock enforce
 *    loop, 2026-06-08: `inboundAge=3389s` on a `● Ready.` session).
 *  - markers flagged `undeliverable: true` — already dead-lettered by the channel.
 *  - markers flagged `discretionary: true` (ENG-6319) — skippable auto-followed
 *    participant-thread inbound; a deliberate skip must not force-respawn a
 *    healthy session.
 *
 * Distinct from `oldestPendingInboundArrivalMs`, which feeds the ENG-6017
 * `pending-inbound-stale` CloudWatch alarm.
 *
 * ENG-10048: this note used to say that alarm's "semantics must NOT change",
 * and it was read as protecting the FILTER — which was fine — while the CLOCK
 * was resettable all along. The comment steering editors away pointed at the
 * half that was already correct, which is part of why the defect survived. The
 * two functions now differ deliberately on BOTH axes:
 *   - clock: the alarm keys on arrival; this one still keys on mtime, because a
 *     wedge is about THIS session's drain behaviour and an in-session rewrite is
 *     genuine evidence of engagement.
 *   - filter: the alarm INCLUDES pre-session markers (a message that outlived a
 *     respawn is more stranded, not less); this one excludes them, because a
 *     leftover cannot prove the current session is failing.
 */
export function oldestLivePendingInboundMtimeMs(
  agentHomeDir: string,
  opts: { sessionStartMs?: number | null } = {},
): number | null {
  const sessionStartMs = opts.sessionStartMs ?? null;
  let oldest: number | null = null;
  let entries;
  try {
    entries = readdirSync(agentHomeDir, { withFileTypes: true });
  } catch {
    return null;
  }
  for (const entry of entries) {
    if (!entry.isDirectory() || !entry.name.endsWith('-pending-inbound')) continue;
    const dir = join(agentHomeDir, entry.name);
    let files;
    try {
      files = readdirSync(dir, { withFileTypes: true });
    } catch {
      continue;
    }
    for (const file of files) {
      if (!file.isFile() || file.name.startsWith('.')) continue;
      const full = join(dir, file.name);
      let mtimeMs: number;
      try {
        mtimeMs = statSync(full).mtimeMs;
      } catch {
        continue; // drained between readdir and stat — happy path
      }
      if (sessionStartMs !== null && mtimeMs < sessionStartMs) continue; // pre-session leftover
      const flags = readMarkerFlags(full);
      if (flags === null) continue; // vanished between stat and read — drained, exclude
      if (flags !== 'malformed' && (flags.undeliverable || flags.discretionary)) continue;
      if (oldest === null || mtimeMs < oldest) oldest = mtimeMs;
    }
  }
  return oldest;
}

/**
 * ENG-10045 — how many DEAD-LETTERED inbound markers are on this host for this
 * agent RIGHT NOW.
 *
 * ## Why this function exists at all
 *
 * `DEAD_LETTERED_INBOUND_HOURLY_METRIC` is a SUM of dead-letter EVENTS over a
 * trailing hour. An hour after the last event the datapoint is zero, CloudWatch
 * fires `OKActions`, the bridge closes the alert — and the undelivered customer
 * message is still sitting in `*-pending-inbound-stale/`, and will be forever.
 * The alarm never measured whether anything was still stranded. This is the
 * gauge that lets it: the count is the artifact's CURRENT population, so an OK
 * means the artifact is gone, which is what the ENG-10045 close contract in
 * `packages/api/src/lib/agent-heartbeat-alarms.ts` requires.
 *
 * ## The predicate, stated rather than inferred
 *
 * Sherlock's correction on ENG-10045 was that `mtime` looked like an oversight
 * and was a deliberate choice nobody had written down, so the next editor
 * "fixed" it into a known bug. Three choices here, each with its reason:
 *
 * 1. **`-stale` dirs only, never the live `*-pending-inbound/` dirs.** A live
 *    marker is a message still queued for delivery — undelivered, but not
 *    stranded. It has its own signal (`PENDING_INBOUND_AGE_METRIC`) and its own
 *    alarm. Counting both here would make this gauge fire for the healthy case
 *    where a message arrived four seconds ago.
 *
 * 2. **`msteams-pending-inbound-stale` IS counted**, even though
 *    `parkPendingInbound` skips the LIVE msteams dir. Those are not the same
 *    decision. The live skip exists because msteams top-level files are raw Bot
 *    Framework activity payloads that the teams channel server re-drains on
 *    boot, so moving them aside defeated the one channel with native respawn
 *    recovery. Nothing re-drains a `-stale` dir: anything that reached it was
 *    put there by a dead-letter, which is exactly the population this counts. A
 *    skip here would make msteams the one channel whose lost messages are
 *    invisible.
 *
 * 3. **A malformed marker IS counted.** It is the same rule this file already
 *    applies in the other direction — `parkPendingInbound` parks a corrupt
 *    marker rather than dead-lettering it, because "corrupt must never become a
 *    drop". The mirror of that is that corrupt must never mask a drop either: a
 *    file we cannot parse, sitting in a dead-letter dir, is a customer message
 *    we cannot prove was delivered. It counts. This also means the count needs
 *    no marker parse at all, which is why it is a `readdir` and not a `readFile`
 *    per entry — it runs on the probe cadence for every agent on the host.
 *
 * ## A FAILED SCAN IS NOT AN EMPTY ONE (CodeRabbit, #5931 — and it was right)
 *
 * The first version returned 0 when `readdirSync` threw, matching every other
 * scan in this file. That is safe for the AGE scans, whose 0 means "no marker so
 * no age" and whose field is omitted anyway. It is NOT safe here, because a 0
 * from this function is published as an affirmative datapoint and an affirmative
 * zero is what CLEARS the alarm. A host that could not read its own marker
 * directory would have told CloudWatch "nothing is stranded" — the same false
 * all-clear as the trailing-hour SUM, arriving by a different route.
 *
 * So the return is `number | null`, and the rule is asymmetric on purpose:
 *
 *   - a count we ESTABLISHED (every stale dir read cleanly) → that number,
 *     including 0, which is the datapoint that legitimately clears the alarm;
 *   - a count of ZERO we could NOT establish (the home or any stale dir threw) →
 *     `null`, "no signal", and the caller omits the field entirely;
 *   - a POSITIVE count with some dir unreadable → still that number. A partial
 *     positive can only under-report, and under-reporting one stranded message
 *     while reporting another still leaves the alarm open. Suppressing it to
 *     `null` would throw away a true alarm to avoid an imprecise one.
 *
 * Paired with `TreatMissingData: 'ignore'` on the alarm (also CodeRabbit), the
 * `null` case means the alarm RETAINS its state rather than drifting to OK. The
 * two halves are one fix: this decides when to say nothing, and that decides
 * what silence means.
 */
export function deadLetteredInboundMarkerCount(agentHomeDir: string): number | null {
  let count = 0;
  let scanIncomplete = false;
  let entries;
  try {
    entries = readdirSync(agentHomeDir, { withFileTypes: true });
  } catch {
    return null; // could not even list the home — we have established nothing
  }
  for (const entry of entries) {
    if (!entry.isDirectory() || !entry.name.endsWith('-pending-inbound-stale')) continue;
    let files;
    try {
      files = readdirSync(join(agentHomeDir, entry.name), { withFileTypes: true });
    } catch {
      scanIncomplete = true;
      continue;
    }
    for (const file of files) {
      // Dotfiles are bookkeeping, never markers — the same exclusion every
      // other scan in this file makes.
      if (!file.isFile() || file.name.startsWith('.')) continue;
      count++;
    }
  }
  // Only a ZERO needs to have been established. A positive is reported however
  // partial it is — see the asymmetry note above.
  if (count === 0 && scanIncomplete) return null;
  return count;
}

/**
 * ENG-6160: age (s) of the oldest LIVE pending-inbound marker for an agent, or
 * null when none. The wedge detector uses this instead of the alarm-facing
 * `pending_inbound_oldest_age_seconds` so a stale/dead-letter marker can't
 * false-fire a respawn.
 */
export function livePendingInboundOldestAgeSeconds(
  codeName: string,
  sessionStartMs: number | null,
  now: Date = new Date(),
): number | null {
  const oldest = oldestLivePendingInboundMtimeMs(dirname(paneLogPath(codeName)), { sessionStartMs });
  if (oldest === null) return null;
  return Math.max(0, Math.floor((now.getTime() - oldest) / 1000));
}

/**
 * ENG-8881: age of the most recent channel inbound ACTIVITY for an agent, in ms
 * since epoch — arrival *or* drain, across every channel.
 *
 * WHY NOT THE OBVIOUS SIGNALS. The incident (ENG-8881) is an agent that accepted
 * work on Telegram and was force-restarted three seconds later, before it could
 * write the kanban row. Neither existing signal sees that moment:
 *
 *   - `lastInboundMs` / `inboundAgeSecondsFor` in manager-worker is stamped ONLY
 *     from `processDirectChatMessage`. Slack and Telegram arrive through MCP
 *     children, so for the channel this incident happened on it is never set.
 *   - `oldestLivePendingInboundMtimeMs` counts UNANSWERED markers. The agent had
 *     already replied, so its marker was drained and the signal was empty at the
 *     instant of the kill. Correctly empty — and useless here.
 *
 * The window we must protect is exactly "a message arrived and was answered just
 * now", which is the moment a marker is created and then removed. Both mutate the
 * CONTAINING DIRECTORY's mtime, so the directory timestamp survives the drain
 * that erases the marker itself. That is the signal this returns.
 *
 * Deliberately reads directory mtimes and not file mtimes: the files are gone by
 * the time we need to know they existed.
 *
 * Returns null when the agent has no channel directories at all (never messaged).
 */
export function newestPendingInboundActivityMtimeMs(agentHomeDir: string): number | null {
  let newest: number | null = null;
  let entries;
  try {
    entries = readdirSync(agentHomeDir, { withFileTypes: true });
  } catch {
    return null;
  }
  for (const entry of entries) {
    // Same predicate as oldestLivePendingInboundMtimeMs, so the two agree on
    // what counts as a channel dir. `-pending-inbound-stale` does not match.
    if (!entry.isDirectory() || !entry.name.endsWith('-pending-inbound')) continue;
    try {
      const mtimeMs = statSync(join(agentHomeDir, entry.name)).mtimeMs;
      if (newest === null || mtimeMs > newest) newest = mtimeMs;
    } catch {
      continue;
    }
  }
  return newest;
}

/**
 * ENG-6160: move every pending-inbound marker for an agent aside into a sibling
 * `<channel>-pending-inbound-stale/` directory (NOT silently deleted — the
 * payload pointer is preserved for forensics), returning the count moved.
 *
 * ENG-6289: no longer called on wedge respawn — the wedge path parks instead
 * (see `parkPendingInbound` above; blanket dead-letter permanently dropped the
 * user's message). Kept as the explicit "move everything aside" seam for tests
 * and operator emergencies. The stale dir does not end in `-pending-inbound`,
 * so neither the probe nor this scan re-counts moved markers.
 */
/**
 * ENG-6289: park-not-drop. On a force-fresh wedge respawn, KEEP undrained
 * pending-inbound markers in their live dirs instead of dead-lettering them —
 * the fresh session's orient hook surfaces them ("N queued messages" + details)
 * and ENG-5969 replay (when enabled) re-pushes their payloads. Markers are NOT
 * rewritten (no counter, no mtime bump): the ENG-6160 pre-session exclusion in
 * `oldestLivePendingInboundMtimeMs` already keeps an untouched parked marker
 * out of the fresh session's wedge signal, and re-delivery stays bounded by
 * the existing machinery — the channel-side `replay_count` cap (≤3) and the
 * orphan sweep's received_at TTL. A manager-side rewrite would be the first
 * cross-process writer into marker files, where a torn read in the channel
 * sweep DELETES the marker (`unlinkSync` on parse failure) — the exact drop
 * this function exists to prevent.
 *
 * Only markers already flagged `undeliverable: true` are dead-lettered (moved
 * to `-stale`) — the channel already gave the user the ⏳ notice for those, so
 * nothing can ever drain them. Malformed markers are LEFT IN PLACE, matching
 * the live-scan philosophy above (a corrupt marker must never mask — or
 * become — a dropped message).
 *
 * The msteams dir is skipped wholesale: its top-level files are raw Bot
 * Framework activity payloads (the transport queue, not bookkeeping — real
 * markers live in the hidden `.markers/` subdir this scan never touches), and
 * the teams channel server's boot drain redelivers them to the fresh session
 * on its own. Moving them aside (what the pre-ENG-6289 dead-letter did) was
 * actively defeating the one channel with native respawn recovery.
 */
export interface ParkPendingInboundResult {
  parked: number;
  deadLettered: number;
}

/**
 * Move one marker into the sibling `-stale` dead-letter dir (moved, not
 * deleted — preserved for forensics). Returns true on success; best-effort —
 * a marker that vanished or can't move is left as-is.
 */
function moveMarkerToStale(dir: string, deadDir: string, name: string): boolean {
  try {
    mkdirSync(deadDir, { recursive: true });
    renameSync(join(dir, name), join(deadDir, name));
    return true;
  } catch {
    return false;
  }
}

export function parkPendingInbound(codeName: string, _now: Date = new Date()): ParkPendingInboundResult {
  const home = dirname(paneLogPath(codeName));
  const result: ParkPendingInboundResult = { parked: 0, deadLettered: 0 };
  let entries;
  try {
    entries = readdirSync(home, { withFileTypes: true });
  } catch {
    return result;
  }
  for (const entry of entries) {
    if (!entry.isDirectory() || !entry.name.endsWith('-pending-inbound')) continue;
    if (entry.name === 'msteams-pending-inbound') continue; // transport queue — boot drain owns recovery
    const dir = join(home, entry.name);
    const deadDir = join(home, `${entry.name}-stale`);
    let files;
    try {
      files = readdirSync(dir, { withFileTypes: true });
    } catch {
      continue;
    }
    for (const file of files) {
      if (!file.isFile() || file.name.startsWith('.')) continue;
      const flags = readMarkerFlags(join(dir, file.name));
      if (flags === null) continue; // drained mid-scan — already gone
      // Only undeliverable markers dead-letter. Discretionary markers
      // (ENG-6319) PARK like engaged ones — they may be a real operator
      // message awaiting a reply (the live silent-loss class); malformed
      // markers park too (corrupt must never become a drop).
      if (flags !== 'malformed' && flags.undeliverable) {
        if (moveMarkerToStale(dir, deadDir, file.name)) result.deadLettered++;
      } else {
        result.parked++;
      }
    }
  }
  return result;
}

export function deadLetterPendingInbound(codeName: string, _now: Date = new Date()): number {
  const home = dirname(paneLogPath(codeName));
  let moved = 0;
  let entries;
  try {
    entries = readdirSync(home, { withFileTypes: true });
  } catch {
    return 0;
  }
  for (const entry of entries) {
    if (!entry.isDirectory() || !entry.name.endsWith('-pending-inbound')) continue;
    const dir = join(home, entry.name);
    const deadDir = join(home, `${entry.name}-stale`);
    let files;
    try {
      files = readdirSync(dir, { withFileTypes: true });
    } catch {
      continue;
    }
    for (const file of files) {
      if (!file.isFile() || file.name.startsWith('.')) continue;
      if (moveMarkerToStale(dir, deadDir, file.name)) moved++;
    }
  }
  return moved;
}

/**
 * Compute the pane.log age for each agent. Missing or unreadable
 * pane.log returns null — the caller should drop those entries
 * rather than fabricate a "fresh" or "ancient" value. A missing
 * file means the agent has never spawned in this manager generation,
 * which is a separate problem covered by SessionAliveAgeSeconds.
 */
export function collectResponsivenessProbes(
  codeNames: string[],
  now: Date = new Date(),
): ResponsivenessProbeResult[] {
  const nowMs = now.getTime();
  const results: ResponsivenessProbeResult[] = [];
  for (const codeName of codeNames) {
    try {
      const panePath = paneLogPath(codeName);
      const mtimeMs = statSync(panePath).mtimeMs;
      const ageSeconds = Math.max(0, Math.floor((nowMs - mtimeMs) / 1000));
      const result: ResponsivenessProbeResult = {
        code_name: codeName,
        pane_activity_age_seconds: ageSeconds,
      };
      attachSupplementalCounters(result, dirname(panePath), nowMs);
      results.push(result);
    } catch {
      // No pane.log yet (fresh agent, never spawned) — skip. The
      // session-alive monitor already covers the "should be running
      // but isn't" case.
      //
      // ENG-8090: an OPENCODE agent lands here on every cycle, permanently —
      // its serve writes `opencode-serve.log`, so this stat always throws. That
      // is what `collectPanelessActivityProbes` exists to cover; this function
      // stays pane-only because the wedge detector consumes its output and must
      // never act on an agent it cannot read a pane age for.
    }
  }
  return results;
}

/**
 * ENG-8090: read + RESET the per-agent counter files that ride the probe's
 * cadence, and attach the ones that produced anything.
 *
 * Extracted from the loop above because these reads were inside the same `try`
 * as the `pane.log` stat: an agent with no `pane.log` lost not just its pane age
 * but its deflection, lane-classification and reply-binding counters too. For
 * opencode that meant those telemetry streams — including the ADR-0024
 * misclassification canary — were silently empty for the whole fleet, a wider
 * blast radius than the liveness gap ENG-8090 was filed for.
 *
 * The reads are DESTRUCTIVE (read-and-reset), so exactly one collector may run
 * them per agent per cycle. That is guaranteed structurally, not by convention:
 * `collectResponsivenessProbes` handles agents whose `pane.log` stat succeeds
 * and `collectPanelessActivityProbes` handles those whose stat fails, and the
 * two sets are complements of each other.
 */
function attachSupplementalCounters(
  result: ResponsivenessProbeResult | ResponsivenessProbePayloadEntry,
  agentHome: string,
  nowMs: number,
): void {
  // ENG-6017: piggyback the pending-inbound drain-age scan on the same
  // cadence. Field omitted (not 0) when there are no markers.
  const oldestMarkerMs = oldestPendingInboundArrivalMs(agentHome, nowMs);
  if (oldestMarkerMs !== null) {
    result.pending_inbound_oldest_age_seconds = Math.max(
      0,
      Math.floor((nowMs - oldestMarkerMs) / 1000),
    );
  }
  // ENG-10045: the stranded-marker gauge, set UNCONDITIONALLY — including 0.
  //
  // Every other field in this function is omitted when empty. This one is not,
  // and the asymmetry is the fix rather than an inconsistency: an omitted field
  // reaches CloudWatch as a missing datapoint, `TreatMissingData: 'notBreaching'`
  // reads that as fine, and the alarm's `OKActions` CLOSE the alert. So for this
  // one signal "I have nothing to say" would be indistinguishable from "nothing
  // is stranded" — which is the exact false all-clear ENG-10045 is about.
  //
  // The one case that IS omitted: a scan we could not complete that found
  // nothing. `null` means "we established nothing", and publishing a 0 there
  // would be the host asserting an all-clear it never checked.
  const stranded = deadLetteredInboundMarkerCount(agentHome);
  if (stranded !== null) result.dead_lettered_inbound_markers = stranded;
  // ENG-8265: the oldest OUTSTANDING interim promise — an inbound the agent
  // acked with `interim: true` and never answered substantively. Rides the same
  // cadence as the pending-inbound scan but is a DIFFERENT signal and must not be
  // conflated with it. ENG-8265 wrote this to explain why the promise clock had
  // to be separate: the pending-inbound age WAS measured from the marker's
  // mtime, which this very ack's in-place rewrite advances, so an ack-then-drop
  // reset that clock while leaving the user unanswered. ENG-10048 fixed that
  // clock for every channel that records `received_at`. It is NOT a blanket
  // immunity: msteams markers carry no `received_at`, so they fall back to
  // mtime and an in-place rewrite still resets their reported age (CodeRabbit
  // on #5691 - the PR body claimed the residual gap, this comment did not).
  // The two remain different signals regardless and must not be conflated.
  // This one is measured
  // from the marker's `promised_at`, is non-destructive (read-only, no reset), and
  // survives the mid-turn respawn that destroyed the work in the motivating
  // incident. Omitted (never 0) when no promise is outstanding.
  const promiseAge = outstandingPromiseOldestAgeSeconds(agentHome, new Date(nowMs));
  if (promiseAge !== null) {
    result.promise_outstanding_oldest_age_seconds = promiseAge;
  }
  // ENG-6327: read + RESET the per-channel deflection counters on the same
  // cadence so each cause's frequency reaches CloudWatch (ChannelDeflections,
  // Cause dimension). Omitted when there were none this window.
  const deflections = readAndResetChannelDeflections(agentHome);
  if (deflections) {
    result.deflections = deflections;
  }
  // ENG-6408 / ADR-0024 Slice 1.5: read + RESET the shadow lane-classification
  // counters on the same cadence so the classification mix (and the
  // misclassification canary) reaches CloudWatch. Omitted when none this window.
  const laneClassifications = readAndResetChannelLaneClassifications(agentHome);
  if (laneClassifications) {
    result.lane_classifications = laneClassifications;
  }
  // WS0: read + RESET the shadow reply-target counters on the same cadence so
  // the cross-thread classification mix reaches CloudWatch. Omitted when none.
  const replyTargetClassifications = readAndResetSlackReplyTargetClassifications(agentHome);
  if (replyTargetClassifications) {
    result.slack_reply_target_classifications = replyTargetClassifications;
  }
  // WS2: read + RESET the shadow reply-binding counters on the same cadence
  // so the bound / coords_corrected / unknown_target mix (the enforce-flip
  // gate) reaches CloudWatch. Omitted when none this window.
  const replyBindingClassifications = readAndResetSlackReplyBindingClassifications(agentHome);
  if (replyBindingClassifications) {
    result.slack_reply_binding_classifications = replyBindingClassifications;
  }
  // ENG-7855: read + RESET the hot-thread-guard outcome counters on the same
  // cadence so the redirect / root / not-applicable mix (the fire rate that
  // tells "correctly quiet" from "never had the chance") reaches CloudWatch.
  // Omitted when none this window (off-mode agents never write these).
  const hotThreadClassifications = readAndResetSlackHotThreadClassifications(agentHome);
  if (hotThreadClassifications) {
    result.slack_hot_thread_classifications = hotThreadClassifications;
  }
  // ENG-8346: read + RESET the direct-chat cursor-advance shortfall counters, so
  // "the reply POST succeeded but the cursor did not move" reaches CloudWatch
  // instead of only a per-host stderr line. Omitted when none this window.
  //
  // It matters that this lives HERE rather than in either collector: an opencode
  // agent has no pane.log, and site 9 (the manager's ADR-0047 arm, which writes
  // the `direct-chat-manager-` file) is the opencode path SPECIFICALLY. Putting
  // the read in `collectResponsivenessProbes` would have collected every agent
  // except the one whose writer this is.
  const cursorShortfalls = readAndResetDirectChatCursorShortfallClassifications(agentHome);
  if (cursorShortfalls) {
    result.direct_chat_cursor_shortfall_classifications = cursorShortfalls;
  }
}

/**
 * ENG-8090: activity probes for agents that have NO readable `pane.log`.
 *
 * The opencode runtime is the whole reason this exists. Its serve is a headless
 * HTTP server, not a tmux TUI, so there is no pane whose mtime tracks agent
 * work — and `opencode-serve.log`, the obvious substitute, is the wrong signal
 * twice over: it advances for HTTP-server reasons unrelated to any turn (so an
 * idle agent would bill as permanently busy), and it kept advancing all through
 * ENG-8058's deadlock (so a wedged agent would read alive). Both consumers are
 * fed from the turn lifecycle instead, via the two `*AgeSeconds` resolvers.
 *
 * Agents whose `pane.log` IS readable are skipped here and reported by
 * `collectResponsivenessProbes` — see the destructive-read note on
 * `attachSupplementalCounters`. A Claude Code agent that has simply never
 * spawned also has no `pane.log`; it lands here, both resolvers return null for
 * it, and it emits an entry carrying only whatever counters exist. That is a
 * strict improvement on today (those counters were dropped), and it writes no
 * activity timestamp, so it cannot make an unspawned agent look alive or busy.
 */
export function collectPanelessActivityProbes(
  codeNames: string[],
  resolvers: {
    /** Seconds since the agent was last occupied; 0 while a turn is in flight. */
    busyActivityAgeSeconds: (codeName: string) => number | null;
    /** Seconds since a turn last produced real reply text. */
    turnCompletionAgeSeconds: (codeName: string) => number | null;
  },
  now: Date = new Date(),
): ResponsivenessProbePayloadEntry[] {
  const nowMs = now.getTime();
  const results: ResponsivenessProbePayloadEntry[] = [];
  for (const codeName of codeNames) {
    const panePath = paneLogPath(codeName);
    try {
      statSync(panePath);
      // Has a pane.log — `collectResponsivenessProbes` owns this agent, and
      // running the destructive counter reads here too would halve its counts.
      continue;
    } catch {
      // No pane.log — this collector owns it. Fall through.
    }
    const entry: ResponsivenessProbePayloadEntry = { code_name: codeName };
    attachSupplementalCounters(entry, dirname(panePath), nowMs);

    // ENG-10045: hold the stranded-marker gauge back until we know whether this
    // entry is going out at all, then re-attach it under the rule stated below.
    //
    // It is the one counter attached UNCONDITIONALLY (an affirmative zero is
    // what clears its alarm), so on a paneless agent with no other signal it was
    // the only field besides the name — and every never-spawned agent started
    // emitting a payload entry every cycle, which is the per-cycle iteration
    // ENG-8090 removed.
    const strandedMarkers = entry.dead_lettered_inbound_markers;
    delete entry.dead_lettered_inbound_markers;

    const busy = resolvers.busyActivityAgeSeconds(codeName);
    if (busy !== null && Number.isFinite(busy)) {
      entry.busy_activity_age_seconds = Math.max(0, Math.floor(busy));
    }
    const turn = resolvers.turnCompletionAgeSeconds(codeName);
    if (turn !== null && Number.isFinite(turn)) {
      entry.turn_completion_age_seconds = Math.max(0, Math.floor(turn));
    }
    // ENG-10045 — THE RULE, stated rather than counted (Koda on #5931).
    //
    //   The gauge RIDES ALONG with an entry that exists on its own merits.
    //   A POSITIVE gauge is itself a reason for the entry to exist.
    //   A ZERO gauge is never a reason.
    //
    // The first draft of this was `Object.keys(entry).length === 2`, which is the
    // same rule expressed as arithmetic over the object's shape — and it is
    // wrong the moment anyone adds another field to this collector, because the
    // magic number silently stops matching and every never-spawned agent starts
    // emitting again. Nothing would fail; the entries would just come back. The
    // form below cannot drift that way: it asks the question directly.
    //
    // Why a zero is safe to drop: there is nothing to clear. An agent this quiet
    // has no alarm state to move, and `TreatMissingData: 'ignore'` means a
    // missing datapoint leaves the alarm exactly where it is. A POSITIVE count
    // must always get through — a stranded message is a signal by definition, and
    // losing it here would be this whole ticket again.
    const hasSignalOfItsOwn = Object.keys(entry).length > 1;
    if (strandedMarkers !== undefined && (strandedMarkers > 0 || hasSignalOfItsOwn)) {
      entry.dead_lettered_inbound_markers = strandedMarkers;
    }
    // Nothing but a name. Sending it would have the API iterate a payload entry
    // that can produce no metric and no write — on every cycle, for every agent
    // that has never spawned. Absent is already the API's "no signal", so say
    // nothing rather than say nothing at length.
    if (Object.keys(entry).length === 1) continue;
    results.push(entry);
  }
  return results;
}
